import { router } from "expo-router";
import { observer } from "mobx-react";
import React, { useMemo } from "react";
import { StyleSheet } from "react-native";
import { Pressable } from "~/components/V2/Pressable";
import { BrandText } from "~/components/V2/Text/BrandText";
import { useStandaloneBookingFlow } from "~/features/book-appointment/useStandaloneBookingFlow";
import { useResponsive } from "~/hooks/useResponsive";
import type { AppointmentReasonInstance } from "~/mobx/entities/appointment-reasons/AppointmentReason";
import { useStore } from "~/mobx/utils/useStore";
import { styleConstantsV2 } from "~/style/styleConstantsV2";
import { bookingDataLayerPush } from "~/utils/logBookingEvent";
import { IconButton } from "../IconButton";
import { Modal } from "../Modal";
import { Spacer } from "../Spacer";
import { ButtonV2 } from "./Button";
import { BodyText } from "./Text/BodyText";
import { View } from "./View";

const S = StyleSheet.create({
  appointmentTypeTitle: {
    fontSize: 20,
    lineHeight: 28,
  },
  appointmentPriceText: {
    fontSize: 20,
    lineHeight: 28,
    color: styleConstantsV2.colorPrimary,
  },
  button: {
    width: "100%",
  },
  titleContainer: {
    marginRight: 40,
  },
});

export const AppointmentTypeModal = observer(function AppointmentTypeModal({
  appointmentType,
  isVisible,
  setIsMoreInfoVisible,
}: {
  appointmentType: AppointmentReasonInstance;
  isVisible: boolean;
  setIsMoreInfoVisible: (isVisible: boolean) => void;
}) {
  const { clinicId, isStandAloneBookingFlow, isNewUser } =
    useStandaloneBookingFlow();
  const store = useStore();
  const language = intl.locale;

  const r = useResponsive();

  const formatter = useMemo(() => {
    if (!appointmentType.defaultPrice?.currencyCode) {
      return null;
    }

    return new Intl.NumberFormat(language, {
      style: "currency",
      currency: appointmentType.defaultPrice?.currencyCode,
    });
  }, [language, appointmentType.defaultPrice?.currencyCode]);

  return (
    <>
      {isVisible && (
        <Modal>
          <Pressable
            flexDirectionRow
            style={{ position: "relative" }}
            flex
            colorBackgroundBackdrop
            centerContent
            onPress={() => {
              setIsMoreInfoVisible(false);
            }}
          >
            <View
              colorWhite
              paddingHorizontal24
              paddingVertical32
              style={{
                maxWidth: 615,
                borderRadius: 16,
                paddingBottom: 64,
                width: r("100%", "100%", 615),
                alignSelf: r("flex-end", "center", "center"),
                borderBottomRightRadius: r(0, 16, 16),
                borderBottomLeftRadius: r(0, 16, 16),
              }}
            >
              <View absoluteTopRight padding24>
                <IconButton
                  iconName="close"
                  iconSize={32}
                  iconColor={styleConstantsV2.colorBlack}
                  onPress={() => {
                    setIsMoreInfoVisible(false);
                  }}
                />
              </View>
              <View style={S.titleContainer}>
                <BrandText weightBold style={S.appointmentTypeTitle}>
                  {appointmentType.name}
                </BrandText>
              </View>
              <Spacer small />
              {typeof appointmentType.defaultPrice?.amount === "number" &&
                !!appointmentType.defaultPrice.currencyCode && (
                  <BodyText weightBold style={S.appointmentPriceText}>
                    {formatter?.format(
                      appointmentType.defaultPrice?.amount ?? 0,
                    )}
                  </BodyText>
                )}

              <Spacer large />
              <BodyText sizeLarge weightRegular colorBlack>
                {appointmentType.description}
              </BodyText>
              <Spacer extraLarge />

              <ButtonV2
                medium
                style={S.button}
                title={intl.formatMessage({
                  id: "AtemAW",
                  defaultMessage: "Select service",
                  description:
                    "Button text to select a service for booking a clinic visit",
                })}
                primary
                onPress={() => {
                  store.bookingStore.setReasonId(appointmentType.id);

                  setIsMoreInfoVisible(false);

                  if (isStandAloneBookingFlow) {
                    // Log the event to the booking data layer
                    bookingDataLayerPush({
                      event: "Booking_web_app",
                      ga_action: "Choose treatment",
                      reason_id: appointmentType.id,
                      reason_name: appointmentType.name,
                    });

                    router.navigate({
                      pathname: "/(standalone)/booking/date-and-time",
                      params: {
                        clinicId,
                        isNewUser: isNewUser ? "1" : "0",
                      },
                    });
                  } else {
                    router.navigate({
                      pathname: "/(app)/book-appointment/date-and-time",
                    });
                  }
                }}
              />
            </View>
          </Pressable>
        </Modal>
      )}
    </>
  );
});
